import React, { PureComponent, ErrorInfo } from 'react';
import styled from 'styled-components';
import store from '@carvana/purchase-store';
import { datadogRum } from '@datadog/browser-rum';
import { CancelCurrentPurchase } from '../../../../purchase-ui/src/components/dashboard/ducks/actions';
import { BlueButton } from '../buttons';
import { Title } from '../titles';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  padding: 4rem 0 2rem;
  min-height: 350px;

  @media (min-width: ${props => props.theme.breakpoints.md}px) {
    padding-top: 3rem;
  }
`;

const BlueRetryButton = styled(BlueButton)`
  width: 20rem;
`;

const ErrorDetails = styled.details`
  white-space: pre-wrap;
`;

const Content = styled.div`
  text-align: center;
  max-width: 32rem;
  padding-right: 2rem;
  padding-left: 2rem;
  color: #6d7184;
  font-weight: 390;
`;

const Text = styled.div`
  padding: 1rem 0 5rem;
  font-size: 1rem;
`;

const ChatContainer = styled.span`
  color: #228be6;
  cursor: pointer;
  text-decoration: underline;
`;

interface ErrorProps {
  error: Error | string | null;
  errorInfo: ErrorInfo;
  message: string | null;
  previousLocation: string | null;
  unableToGetTerms: boolean;
  routeToSearch: boolean;
}

class ErrorDisplay extends PureComponent<ErrorProps> {
  static defaultProps = {
    error: null,
    errorInfo: {},
    message: null,
    previousLocation: null,
    unableToGetTerms: false,
    routeToSearch: false
  };

  state = {
    isLoading: false
  };

  componentDidMount() {
    const { error, errorInfo = {}, message, previousLocation } = this.props;
    const errorToReport =
      error === null ? message || 'Unable to determine error message' : error;
    datadogRum.addError(errorToReport, {
      errorInfo,
      message,
      previousLocation,
      customType: 'oopsieDaisy'
    });
  }

  routeToNext = async () => {
    window.location.href = '/acquisition/dashboard';
    return null;
  };

  routeToPaymentType = async () => {
    this.setState({ isLoading: true });
    window.location.href = '/purchase/payment-options/type';
    return null;
  };

  cancelPurchaseandRouteToSearch = async () => {
    await store.dispatch(
      CancelCurrentPurchase({
        reasons: ['VehicleIssues']
      })
    );
    window.location.href = '/cars';
  };

  render() {
    const {
      error,
      errorInfo,
      message,
      unableToGetTerms,
      routeToSearch
    } = this.props;
    const { isLoading } = this.state;
    return (
      <Wrapper>
        <Title>We've hit a speed bump</Title>

        {process.env.NODE_ENV !== 'production' && error && (
          <ErrorDetails open>
            {error && error instanceof Error && error.stack}
            <br />
            Component Stack:
            {errorInfo.componentStack}
          </ErrorDetails>
        )}

        <Content>
          <Text>
            {message ? message : "We're sorry, something went wrong. "}
            You can{' '}
            <ChatContainer onClick={() => CarvanaAiChat.show()}>
              chat
            </ChatContainer>{' '}
            with us or call 1-888-415-0755 for more help.
          </Text>

          <BlueRetryButton
            data-cv-test="backButton"
            onClick={
              // eslint-disable-next-line no-nested-ternary
              unableToGetTerms
                ? this.routeToPaymentType
                : routeToSearch
                ? this.cancelPurchaseandRouteToSearch
                : this.routeToNext
            }
            isBusy={isLoading}
            disabled={isLoading}
          >
            Try Again
          </BlueRetryButton>
        </Content>
      </Wrapper>
    );
  }
}

export default ErrorDisplay;
